import React, { forwardRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
// The printable payslip (.payslip-sheet and friends) is captured to PDF via html2canvas, so it
// keeps its exact print-style markup/CSS rather than being re-skinned in Tailwind.
import '../styles/Dashboard.css';

export function formatCurrency(value) {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatDisplayAmount(value, fallback = 'NA') {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'number' && Number.isNaN(value)) return fallback;
  return typeof value === 'number' ? formatCurrency(value) : value;
}

/**
 * The company payslip template, shared by the admin's Manual Payslip page and the employee's
 * Payslip page so both show the same rows and layout.
 *
 * settings: the client's payslip settings (company name/address/contact).
 * logoUrl: object URL of the uploaded logo, or null to show the company name instead.
 * payslip: {
 *   employeeName, employeeCode, monthLabel, year,
 *   designation, location, department, grade, panNumber, gender, doj,
 *   payableDays, workingDays, lop, pfNumber, uanNumber, bankName, bankAccount,
 *   earnings: [{ label, actual, earned }], deductions: [{ label, amount }],
 *   variablePay, totalActual, totalEarned (without variable pay), totalDeductions, netPay
 * }
 */
export const PayslipDocument = forwardRef(function PayslipDocument({ settings, logoUrl, payslip }, ref) {
  const variablePay = Number(payslip.variablePay) || 0;

  return (
    <div className="payslip-preview keep-light">
      <div className="payslip-sheet" ref={ref}>
        <div className="payslip-border">
          <div className="payslip-top-grid">
            <div className="payslip-logo-box">
              {logoUrl ? (
                <img src={logoUrl} alt="Company logo" className="payslip-logo-image" />
              ) : (
                <div className="payslip-logo-text">
                  <div className="payslip-logo-main">{settings.companyName || 'COMPANY'}</div>
                </div>
              )}
            </div>
            <div className="payslip-company-box">
              <div className="payslip-bluebar payslip-company-title">{settings.companyName}</div>
              <div className="payslip-company-content">
                <p>{settings.addressLine1}</p>
                <p>{settings.addressLine2}</p>
                <p>{settings.addressLine3}</p>
                <p>{settings.phone}</p>
                <p className="payslip-link">{settings.website}</p>
                <p className="payslip-link">{settings.email}</p>
              </div>
            </div>
          </div>

          <div className="payslip-bluebar payslip-month-bar">
            Pay Slip for the Month of {payslip.monthLabel} {payslip.year}
          </div>

          <table className="payslip-details-table">
            <tbody>
              <tr>
                <td className="label">Name</td>
                <td className="value">{payslip.employeeName}</td>
                <td className="label">Emp.Code</td>
                <td className="value">{payslip.employeeCode || 'N/A'}</td>
              </tr>
              <tr>
                <td className="label">Designation</td>
                <td className="value">{payslip.designation}</td>
                <td className="label">Location</td>
                <td className="value">{payslip.location}</td>
              </tr>
              <tr>
                <td className="label">Department</td>
                <td className="value">{payslip.department}</td>
                <td className="label">Grade</td>
                <td className="value">{payslip.grade}</td>
              </tr>
              <tr>
                <td className="label">PAN</td>
                <td className="value">{payslip.panNumber}</td>
                <td className="label">Payable Days</td>
                <td className="value">{payslip.payableDays}</td>
              </tr>
              <tr>
                <td className="label">Gender</td>
                <td className="value">{payslip.gender}</td>
                <td className="label">Working Days</td>
                <td className="value">{payslip.workingDays}</td>
              </tr>
              <tr>
                <td className="label">D.O.J</td>
                <td className="value">{payslip.doj}</td>
                <td className="label">LOP</td>
                <td className="value">{payslip.lop}</td>
              </tr>
              <tr>
                <td className="label">PF No</td>
                <td className="value">{payslip.pfNumber}</td>
                <td className="label">UAN No</td>
                <td className="value">{payslip.uanNumber}</td>
              </tr>
              <tr>
                <td className="label">Bank Name</td>
                <td className="value">{payslip.bankName}</td>
                <td className="label">Bank A/C</td>
                <td className="value">{payslip.bankAccount}</td>
              </tr>
            </tbody>
          </table>

          <table className="payslip-salary-table">
            <thead>
              <tr>
                <th>Earnings</th>
                <th className="amount">Actuals</th>
                <th className="amount">Earned</th>
                <th>Deductions</th>
                <th className="amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({
                length: Math.max(payslip.earnings.length, payslip.deductions.length),
              }).map((_, index) => {
                const earning = payslip.earnings[index];
                const deduction = payslip.deductions[index];
                return (
                  <tr key={`${earning?.label || ''}-${deduction?.label || ''}-${index}`}>
                    <td>{earning?.label || ''}</td>
                    <td className="amount">{earning ? formatCurrency(earning.actual) : ''}</td>
                    <td className="amount">{earning ? formatCurrency(earning.earned) : ''}</td>
                    <td>{deduction?.label || ''}</td>
                    <td className="amount">{deduction ? formatDisplayAmount(deduction.amount) : ''}</td>
                  </tr>
                );
              })}
              {variablePay > 0 && (
                <tr>
                  <td>Variable Pay</td>
                  <td className="amount">{formatCurrency(variablePay)}</td>
                  <td className="amount">{formatCurrency(variablePay)}</td>
                  <td></td>
                  <td className="amount"></td>
                </tr>
              )}
              <tr className="total-row">
                <td>Total(INR)</td>
                <td className="amount">{formatCurrency(payslip.totalActual)}</td>
                <td className="amount">{formatCurrency(payslip.totalEarned + variablePay)}</td>
                <td>Total Deductions(INR)</td>
                <td className="amount">{formatCurrency(payslip.totalDeductions)}</td>
              </tr>
              <tr className="net-row">
                <td colSpan="2">Net Pay(INR)</td>
                <td colSpan="3" className="net-value">{formatCurrency(payslip.netPay)}</td>
              </tr>
            </tbody>
          </table>

          <p className="payslip-footer">
            This is a system generated payslip and does not require authentication
          </p>
        </div>
      </div>
    </div>
  );
});

// Captures a rendered payslip element and saves it as an A4 PDF, spilling onto extra pages
// if it's taller than one page.
export async function downloadElementAsPdf(element, fileName = 'Payslip.pdf') {
  if (!element) return;

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
  });
  const imgData = canvas.toDataURL('image/png');

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = 210;
  const pageHeight = 297;
  const imgWidth = pdfWidth;
  const imgHeight = (canvas.height * pdfWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(fileName);
}
